import React from 'react'
import {
  inputBaseClasses,
  selectClasses,
  Stack,
  StackProps,
  styled,
  Typography,
  unstable_composeClasses,
} from '@mui/material'
import classnames from 'classnames'

import { getFieldWrapperUtilityClass } from './fieldWrapperClasses'

export interface FieldWrapperProps extends StackProps {
  fieldId: string
  label?: string | React.ReactNode
  labelClassName?: string
  children: React.ReactNode
  variant?: 'horizontal' | 'vertical'
  size?: 'small' | 'medium' | 'large'
}

const FieldWrapperRoot = styled(Stack, {
  name: 'FieldWrapper',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles.root, styles[props.variant], styles.label],
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'size',
})<Pick<FieldWrapperProps, 'variant' | 'size'>>(({ variant, size, theme }) => ({
  ...(variant === 'horizontal' && {
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: '0.5rem',

    '& label': {
      flexBasis: '30%',
    },

    '& label + div': {
      flexBasis: '70%',
      marginLeft: 0,
    },
  }),

  ...(variant === 'vertical' && {
    flex: 1,
    alignItems: 'stretch',
    height: '100%',

    '& label': {
      lineHeight: 1.4,
      ...(size === 'large' && {
        fontSize: theme.typography.pxToRem(14),
      }),
    },

    '& label + div': {
      marginTop: '0.25rem',
    },

    ...(size === 'large' && {
      [`& input, & .${inputBaseClasses.input}, & .${inputBaseClasses.input}.${selectClasses.select}`]: {
        height: '3rem',
        fontSize: theme.typography.pxToRem(16),
        lineHeight: '3rem',
      },
    }),
  }),
}))

const useUtilityClasses = (ownerState: Partial<FieldWrapperProps>) => {
  const slots = {
    root: ['root', ownerState.variant, ownerState.size],
    label: ['label'],
  }

  return unstable_composeClasses(slots, getFieldWrapperUtilityClass, ownerState.classes)
}

const FieldWrapper = (props: FieldWrapperProps) => {
  const { className, classes, fieldId, label, labelClassName, children, ...rootProps } = props

  const slotClasses = useUtilityClasses({ classes, size: rootProps.size, variant: rootProps.variant })

  const stackProps = {
    direction: rootProps.variant === 'horizontal' ? 'row' : 'column',
    alignItems: 'center',
    spacing: 1,
    width: '100%',
    flexWrap: 'wrap',
  } as const

  return (
    <FieldWrapperRoot {...stackProps} {...rootProps} className={classnames(slotClasses.root, className)}>
      {label && (
        <Typography
          variant="body2"
          component="label"
          id={`${fieldId}-label`}
          htmlFor={fieldId}
          className={classnames(slotClasses.label, labelClassName)}
        >
          {label}
        </Typography>
      )}

      {children}
    </FieldWrapperRoot>
  )
}

export default React.memo(FieldWrapper) as typeof FieldWrapper
